import { Checkbox, Divider, Tabs } from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import isEmpty from 'lodash/isEmpty';
import React, { MouseEvent, useEffect, useMemo, useState } from 'react';
import { useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';
import { DEFAULT_DEBOUNCE_RATE } from '../../../Base.constants';
import ArticlePreview from '../../../components/ArticlePreview';
import {
  getArticlePreviewHandler,
  OpenArticlePreviewOnClickKey,
} from '../../../components/ArticlePreview/ArticlePreview.logic';
import FiltersBar from '../../../components/FiltersBar';
import { getRecentlyPublishedFilters, transformFilter } from '../../../components/FiltersBar/constants';
import { CONTENT_TYPE_FILTER_URL_KEY } from '../../../components/FiltersBar/FiltersBar.logic';
import ResultsFilter from '../../../components/ResultsFilter/ResultsFilter';
import useCheckArticleAccessStatus from '../../../hooks/articles/useCheckArticleAccessStatus';
import { useBreakpoint, useDocumentTitle, useLocalStorage } from '../../../hooks/common';
import useFetchUserPermissions from '../../../hooks/permissions/useFetchUserPermissions';
import { ContentTypeFilter, ContentTypeTag, Filters, IssuerType, PageType } from '../../../models';
import { ArticleTableRow } from '../../../models/ArticleTableRow';
import { shouldNavigate } from '../../../utils/navigationUtils';
import { isCompanyTag } from '../../../utils/tagUtils';
import ArticleList from '../../Feeds/ArticleList';
import DisplayTypeSelection, { DisplayType } from '../../Feeds/FeedLeftContainer/DisplayTypeSelection';
import { CompanyOutletContext } from '../Company';
import { useRecentlyPublishedContext } from '../CompanyRecentlyPublishedContext';
import {
  contentTypeFiltersMap,
  getInitialActiveTab,
  getRecentlyPublishedTabs,
  viewConfig,
} from '../RecentlyPublished.logic';
import styles from './CompanyRecentlyPublished.module.scss';

const cx = classNames.bind(styles);

const ARTICLES_PER_PAGE = [100, 200];

const CompanyRecentlyPublishedTab = (): React.ReactElement => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [rawFilters, setFilters] = useState<Filters | null>(null);
  const setFiltersDebounced = useDebouncedCallback((filters: Filters | null) => {
    setFilters(filters);
  }, DEFAULT_DEBOUNCE_RATE);
  const { issuerTag, company } = useOutletContext<CompanyOutletContext>();
  const issuerType = isCompanyTag(issuerTag) ? IssuerType.Company : IssuerType.Municipal;
  const { displayType, setDisplayType } = useRecentlyPublishedContext();
  const [openArticlePreviewOnClick, setOpenArticlePreviewOnClick] = useLocalStorage<boolean>(
    OpenArticlePreviewOnClickKey,
    true
  );
  const [previewArticleId, setPreviewArticleId] = useState<number | null>(null);
  const { isDesktop } = useBreakpoint();
  const navigate = useNavigate();
  const showArticlePreview = !!(isDesktop && previewArticleId);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [articleCount, setArticleCount] = useState(ARTICLES_PER_PAGE[0]);
  const [page, setPage] = useState(1);

  const documentTitle = company ? `${company.displayName}: Recently Published` : '';
  useDocumentTitle(documentTitle);
  const { data: userPermissions } = useFetchUserPermissions();

  const articleTypeFilter =
    (searchParams.get(CONTENT_TYPE_FILTER_URL_KEY) as ContentTypeFilter) ||
    getInitialActiveTab(userPermissions!.userSubscription, issuerType);

  const validFilters = Object.values(ContentTypeFilter);

  const filters = useMemo(() => {
    if (rawFilters) {
      if (isEmpty(rawFilters.tags)) {
        return {
          ...rawFilters,
          tags: [contentTypeFiltersMap[ContentTypeFilter.ALL]] as Record<number, ContentTypeTag[]>,
        };
      }
    }

    return rawFilters;
  }, [rawFilters]);

  useEffect(() => {
    setPage(1);
  }, [filters, articleCount, issuerTag]);

  useEffect(() => {
    const userSubscriptions = userPermissions!.userSubscription;
    const contentTypeFilter = searchParams.get(CONTENT_TYPE_FILTER_URL_KEY);
    if (contentTypeFilter === null && userSubscriptions) {
      const initialTab = getInitialActiveTab(userPermissions!.userSubscription, issuerType);
      searchParams.set(CONTENT_TYPE_FILTER_URL_KEY, initialTab);
      setSearchParams(searchParams);
    }
  }, [userPermissions!.userSubscription, issuerType]);

  const checkArticleAccessStatus = useCheckArticleAccessStatus();

  const setArticleTypeFilter = (type: ContentTypeFilter) => {
    if (articleTypeFilter !== type) {
      searchParams.set(CONTENT_TYPE_FILTER_URL_KEY, type);
      setSearchParams(searchParams);
    }
  };

  const tabs = getRecentlyPublishedTabs(articleTypeFilter, setArticleTypeFilter, userPermissions!.permissions);

  const onArticleClick = (article: ArticleTableRow, event: MouseEvent<HTMLElement>) => {
    const articlePreviewHandler = getArticlePreviewHandler(
      setPreviewArticleId,
      isDesktop,
      navigate,
      checkArticleAccessStatus,
      openArticlePreviewOnClick
    );
    articlePreviewHandler(article, event);
  };

  useEffect(() => {
    if (!validFilters.includes(articleTypeFilter)) {
      navigate(`?type=${ContentTypeFilter.ALL}`);
    }
  }, [articleTypeFilter]);

  const setPaginationParameters = (articleCount: number) => {
    setPage(1);
    setArticleCount(articleCount);
  };

  return (
    <div className={cx('body', { 'is-expanded': isExpanded })}>
      <header className={cx('header')}>
        <div className={cx('header-line')}>
          <Tabs tabs={tabs} />
          <div className={cx('action-buttons')}>
            {isDesktop && (
              <Checkbox
                className={cx('article-preview-toggle', 'no-print')}
                dataTestId="article-preview-toggle"
                onClick={() => setOpenArticlePreviewOnClick(!openArticlePreviewOnClick)}
                checked={openArticlePreviewOnClick}
                toggle
                label="Show article previews"
                aria-label="Show article previews"
              />
            )}
            <DisplayTypeSelection selected={displayType} onSelect={setDisplayType} />
            <ResultsFilter
              values={ARTICLES_PER_PAGE}
              onResultsCountChange={setPaginationParameters}
              selectedValue={articleCount}
            />
          </div>
        </div>
        <FiltersBar
          onChange={setFiltersDebounced}
          filterTypes={getRecentlyPublishedFilters(articleTypeFilter, PageType.RecentlyPublished)}
          transformFilter={transformFilter}
          articleType={articleTypeFilter}
          contentTypeFiltersMap={contentTypeFiltersMap}
          translate
        />
        <Divider />
      </header>
      <div className={cx('content', { 'is-expanded': isExpanded })}>
        <ArticleList
          viewConfig={viewConfig(
            onArticleClick,
            articleTypeFilter === ContentTypeFilter.ALL,
            userPermissions!,
            !shouldNavigate(isDesktop, openArticlePreviewOnClick)
          )}
          issuerTag={issuerTag}
          expanded={displayType === DisplayType.Expanded}
          setHasResults={(hasResults) => setIsExpanded(!hasResults)}
          filters={filters}
          articleCount={articleCount}
          page={page}
          setPage={setPage}
          contentTypeFilter={articleTypeFilter}
        />
      </div>
      {previewArticleId && (
        <ArticlePreview articleId={previewArticleId} setArticleId={setPreviewArticleId} show={showArticlePreview} />
      )}
    </div>
  );
};

export default CompanyRecentlyPublishedTab;
